import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RocketLaunch as RocketIcon,
  Star as StarIcon,
  ShieldMoon as ShieldIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../auth/hooks/useAuth';
import useSubscriptionStore, { Plan } from '../../../store/subscriptionStore';
import { fetchPlans, createCheckoutSession } from '../api/subscriptionApi';
import { formatNumber } from '../../../utils/formatters';
import brand from '../../../config/brand';
import clarityService from '../../../services/clarityService';
import { saveCheckoutIntent, readCheckoutIntent, clearCheckoutIntent } from '../utils/checkoutIntent';

const PLAN_ICONS: Record<string, React.ReactNode> = {
  scout: <BoltIcon />,
  cadet: <RocketIcon />,
  captain: <StarIcon />,
  air_marshal: <ShieldIcon />,
};

const PLAN_COLORS: Record<string, string> = {
  scout: '#78909c',
  cadet: '#42a5f5',
  captain: brand.colors.fire,
  air_marshal: '#ab47bc',
};

/** Static fallback so the page renders even before / without the API */
const FALLBACK_PLANS: Plan[] = [
  {
    key: 'scout',
    name: 'Scout',
    price: 0,
    priceAnnual: null,
    annualTotal: null,
    credits: 100000,
    monthlyAllocation: 0,
    features: ['100,000 CP sign-up bonus', 'Access to all models', 'Pay-as-you-go top-ups', 'Community support'],
    firstMonthPromo: null,
  },
  {
    key: 'cadet',
    name: 'Cadet',
    price: 19.99,
    priceAnnual: 15.99,
    annualTotal: 191.88,
    credits: 800000,
    monthlyAllocation: 1,
    features: ['800,000 CP/month', 'All models included', 'Credits roll over (up to 3x)', 'First month free', 'Priority support'],
    firstMonthPromo: 'Free',
  },
  {
    key: 'captain',
    name: 'Captain',
    price: 49.99,
    priceAnnual: 39.99,
    annualTotal: 479.88,
    credits: 2000000,
    monthlyAllocation: 2.5,
    features: ['2,000,000 CP/month', 'All models included', 'Credits roll over (up to 3x)', 'First month $1', 'Priority support', 'Advanced analytics'],
    firstMonthPromo: '$1',
  },
  {
    key: 'air_marshal',
    name: 'Air Marshal',
    price: 149.99,
    priceAnnual: 119.99,
    annualTotal: 1439.88,
    credits: 6600000,
    monthlyAllocation: 7.5,
    features: ['6,600,000 CP/month', 'All models included', 'Credits roll over (up to 3x)', 'Dedicated support', 'Advanced analytics', 'Early access to new features'],
    firstMonthPromo: null,
  },
];

const PAYG_PACKS = [
  { key: 'pack_5', amount: 5, credits: '100,000' },
  { key: 'pack_10', amount: 10, credits: '200,000' },
  { key: 'pack_25', amount: 25, credits: '500,000' },
];

type Billing = 'monthly' | 'annual';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, bootstrapped } = useAuth();
  const { plans: apiPlans, error } = useSubscriptionStore();
  const [billing, setBilling] = useState<Billing>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [resumeNotice, setResumeNotice] = useState<string | null>(null);
  const resumedRef = useRef(false);

  const annual = billing === 'annual';

  // Use API plans if available, otherwise fall back to static data
  const plans = apiPlans && apiPlans.length > 0 ? apiPlans : FALLBACK_PLANS;

  useEffect(() => {
    fetchPlans().catch(() => {});
  }, []);

  const startCheckout = async (planKey: string, cycle: Billing) => {
    setCheckoutLoading(planKey);
    setCheckoutError(null);
    clarityService.track('checkout_start', planKey);
    try {
      const { url } = await createCheckoutSession(planKey, cycle);
      if (url) window.location.href = url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      const msg = err?.response?.data?.error || err?.message || 'Could not start checkout. Please try again.';
      setCheckoutError(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  // A visitor who chose a plan before signing up lands back here signed in:
  // continue the checkout for them instead of making them pick again.
  useEffect(() => {
    if (!user || !bootstrapped || resumedRef.current) return;
    const intent = readCheckoutIntent();
    if (!intent) return;
    resumedRef.current = true;
    clearCheckoutIntent();
    clarityService.track('checkout_resumed', intent.payg || intent.planKey);
    if (intent.payg) {
      navigate('/payment', { state: { payg: intent.payg } });
      return;
    }
    const plan = plans.find((p) => p.key === intent.planKey);
    if (!plan || plan.key === 'scout') return;
    setBilling(intent.billing);
    setResumeNotice(`Continuing your ${plan.name} checkout…`);
    startCheckout(plan.key, intent.billing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, bootstrapped]);

  const goSignUp = () => {
    navigate('/register', { state: { from: { pathname: '/pricing' } } });
  };

  const handleSubscribe = (planKey: string) => {
    clarityService.track('pricing_plan_click', planKey);
    if (planKey === 'scout') {
      if (!user) {
        clarityService.track('cta_click', 'pricing_start_free');
        navigate('/register');
      }
      return;
    }
    if (!user) {
      // Remember the choice, get an account, come back and finish.
      saveCheckoutIntent({ planKey, billing });
      goSignUp();
      return;
    }
    startCheckout(planKey, billing);
  };

  const handlePayg = (packKey: string) => {
    clarityService.track('payg_click', packKey);
    if (!user) {
      saveCheckoutIntent({ planKey: 'payg', billing, payg: packKey });
      goSignUp();
      return;
    }
    navigate('/payment', { state: { payg: packKey } });
  };

  const getPrice = (plan: Plan) => {
    if (plan.price === 0) return 'Free';
    if (annual && plan.priceAnnual) return `$${plan.priceAnnual.toFixed(2)}/mo`;
    return `$${plan.price.toFixed(2)}/mo`;
  };

  const getCta = (plan: Plan) => {
    if (plan.key === 'scout') return user ? 'Included with your account' : 'Start free';
    if (plan.firstMonthPromo === 'Free') return 'Start free month';
    if (plan.firstMonthPromo) return `Start at ${plan.firstMonthPromo}`;
    return 'Subscribe';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {checkoutError && (
        <Alert severity="error" onClose={() => setCheckoutError(null)} sx={{ mb: 3 }}>
          {checkoutError}
        </Alert>
      )}
      {resumeNotice && !checkoutError && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 3 }}>
          {resumeNotice}
        </Alert>
      )}

      {/* Header */}
      <Box textAlign="center" mb={5}>
        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom>
          Simple, transparent pricing
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 620, mx: 'auto', mb: 2 }}>
          Start free with 100,000 credit points and no card. Upgrade when your agents need more room.
          Every plan includes every AI model.
        </Typography>

        {!user && bootstrapped && (
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              clarityService.track('cta_click', 'pricing_header_start_free');
              navigate('/register');
            }}
            sx={{
              mb: 3,
              px: 4,
              fontWeight: 700,
              background: brand.gradients.button,
              color: brand.buttonTextColor,
              '&:hover': { background: brand.gradients.buttonHover },
            }}
          >
            Create your free account
          </Button>
        )}

        {/* Billing period: a real segmented control, so the "Annual" label
            is itself the thing to click (the old switch label drew dead clicks). */}
        <Box>
          <ToggleButtonGroup
            exclusive
            value={billing}
            onChange={(_, v: Billing | null) => v && setBilling(v)}
            size="small"
            color="primary"
            aria-label="Billing period"
          >
            <ToggleButton value="monthly" sx={{ px: 2.5, textTransform: 'none', fontWeight: 600 }}>
              Monthly
            </ToggleButton>
            <ToggleButton value="annual" sx={{ px: 2.5, textTransform: 'none', fontWeight: 600, gap: 1 }}>
              Annual
              <Chip label="Save 20%" size="small" color="success" sx={{ height: 20 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Plan Cards — the whole card is the button */}
      <Grid container spacing={3} justifyContent="center" alignItems="stretch">
        {plans.map((plan) => {
          const color = PLAN_COLORS[plan.key] || '#78909c';
          const isPopular = plan.key === 'captain';
          const isCurrent = plan.key === 'scout' && !!user;
          const busy = checkoutLoading === plan.key;
          const interactive = !isCurrent && !busy;
          return (
            <Grid item xs={12} sm={6} md={3} key={plan.key}>
              <Paper
                elevation={isPopular ? 8 : 2}
                role={interactive ? 'button' : undefined}
                tabIndex={interactive ? 0 : undefined}
                aria-label={interactive ? `${getCta(plan)} — ${plan.name}` : undefined}
                onClick={() => interactive && handleSubscribe(plan.key)}
                onKeyDown={(e) => {
                  if (!interactive) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSubscribe(plan.key);
                  }
                }}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: isPopular ? `2px solid ${color}` : '1px solid',
                  borderColor: isPopular ? color : 'divider',
                  cursor: interactive ? 'pointer' : 'default',
                  transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                  outline: 'none',
                  ...(interactive && {
                    '&:hover, &:focus-visible': {
                      transform: 'translateY(-4px)',
                      boxShadow: 8,
                      borderColor: color,
                    },
                  }),
                }}
              >
                {isPopular && (
                  <Chip
                    label="Most popular"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: color,
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}

                {/* Plan Header */}
                <Box textAlign="center" mb={2}>
                  <Box sx={{ color, mb: 1, '& svg': { fontSize: 36 } }}>
                    {PLAN_ICONS[plan.key]}
                  </Box>
                  <Typography variant="h5" component="h2" fontWeight={700}>
                    {plan.name}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ mt: 1, color }}>
                    {getPrice(plan)}
                  </Typography>
                  {annual && plan.annualTotal ? (
                    <Typography variant="caption" color="text.secondary">
                      ${plan.annualTotal}/year billed annually
                    </Typography>
                  ) : plan.price === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      forever, no card needed
                    </Typography>
                  ) : null}
                  {plan.credits > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {formatNumber(plan.credits)} credits{plan.price === 0 ? ' to start' : '/month'}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Features */}
                <List dense sx={{ flex: 1, py: 0 }}>
                  {plan.features.map((feature, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckIcon sx={{ fontSize: 18, color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* CTA */}
                <Button
                  fullWidth
                  variant={plan.key === 'scout' ? 'outlined' : 'contained'}
                  disabled={!interactive}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSubscribe(plan.key);
                  }}
                  sx={{
                    mt: 2,
                    py: 1.2,
                    fontWeight: 600,
                    ...(plan.key !== 'scout' && {
                      bgcolor: color,
                      '&:hover': { bgcolor: color, filter: 'brightness(0.9)' },
                    }),
                  }}
                >
                  {busy ? <CircularProgress size={22} color="inherit" /> : getCta(plan)}
                </Button>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* PAYG Section */}
      <Box mt={8} textAlign="center">
        <Typography variant="h4" component="h2" fontWeight={700} gutterBottom>
          Need extra credits?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
          Top up any time with a pay-as-you-go credit pack. Works with every plan, including the free one.
        </Typography>

        <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 700, mx: 'auto' }}>
          {PAYG_PACKS.map((pack) => (
            <Grid item xs={12} sm={4} key={pack.key}>
              <Paper
                role="button"
                tabIndex={0}
                aria-label={`Buy ${pack.credits} credits for $${pack.amount}`}
                onClick={() => handlePayg(pack.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePayg(pack.key);
                  }
                }}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover, &:focus-visible': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: 'primary.main' },
                }}
              >
                <Typography variant="h5" fontWeight={700}>
                  ${pack.amount}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {pack.credits} credits
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePayg(pack.key);
                  }}
                >
                  Buy now
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default PricingPage;
